/**
 * The builder page's draft spec: pure helpers for adding, moving and
 * re-typing tiles, and the shared zod's messages laid out by where they show.
 *
 * @module components/looker/admin/builder/builderDraft
 */
import {
  SPEC_MAX_TILES, TemplateSpecSchema, VIS_SHAPE, tileFields, type SpecTile, type SpecVis, type TemplateSpec,
} from '@/services/looker/lookerTemplateSpec';

export const EMPTY_SPEC: TemplateSpec = { title: '', description: '', tiles: [] };

/** A fresh tile with the first free "tile-n" key. */
export const newTile = (tiles: readonly SpecTile[]): SpecTile => {
  const keys = new Set(tiles.map((t) => t.key));
  let n = tiles.length + 1;
  while (keys.has(`tile-${n}`)) n += 1;
  return { key: `tile-${n}`, title: '', vis: 'single_value', measures: [], width: 'half' };
};

export const canAddTile = (spec: TemplateSpec): boolean => spec.tiles.length < SPEC_MAX_TILES;

export const moveTile = (tiles: readonly SpecTile[], from: number, to: number): SpecTile[] => {
  if (from < 0 || to < 0 || from >= tiles.length || to >= tiles.length || from === to) return [...tiles];
  const rest = tiles.filter((_, i) => i !== from);
  return [...rest.slice(0, to), tiles[from], ...rest.slice(to)];
};

/** An app box: no fields or query options (showWhen included), a widget (the one it had, else outcomes), never one sixth wide. */
const toAppTile = (tile: SpecTile): SpecTile => ({
  key: tile.key,
  title: tile.title,
  vis: 'app',
  measures: [],
  widget: tile.vis === 'app' ? tile.widget ?? 'outcomes' : 'outcomes',
  width: tile.width === 'sixth' ? 'third' : tile.width,
});

/**
 * The tile as the new vis can take it: fields the vis has no place for are
 * dropped (a dimension, a pivot, measures past its maximum), and a sort on a
 * dropped field goes with it. What the vis still lacks is left for the
 * validation message to name.
 */
export const withVis = (tile: SpecTile, vis: SpecVis): SpecTile => {
  if (vis === 'app') return toAppTile(tile);
  const shape = VIS_SHAPE[vis];
  const {
    dimension, pivot, sort, extraDimensions, compare, compareMeasures, lineMeasures, rightAxis, autoGrain, shareOf, fadeBelow, listAbsent,
    widget: _widget, ...rest
  } = tile;
  const measures = shape.measures.max === null ? tile.measures : tile.measures.slice(0, shape.measures.max);
  const combo = vis === 'line' || vis === 'area' || vis === 'column';
  const next: SpecTile = {
    ...rest,
    vis,
    measures,
    width: tile.width === 'sixth' && vis !== 'single_value' ? 'third' : tile.width,
    ...(dimension && shape.dimension !== 'none' ? { dimension } : {}),
    ...(pivot && shape.pivot ? { pivot } : {}),
    ...(vis === 'table' && extraDimensions ? { extraDimensions } : {}),
    ...((vis === 'table' || vis === 'single_value') && compare ? { compare } : {}),
    ...(vis === 'table' && compareMeasures ? { compareMeasures } : {}),
    ...(combo && lineMeasures ? { lineMeasures } : {}),
    ...(combo && rightAxis ? { rightAxis } : {}),
    ...(combo && autoGrain ? { autoGrain } : {}),
    ...(vis === 'table' && shareOf ? { shareOf } : {}),
    ...(vis === 'table' && fadeBelow ? { fadeBelow } : {}),
    ...((vis === 'pie' || vis === 'donut') && listAbsent ? { listAbsent } : {}),
  };
  return sort && tileFields(next).includes(sort.field) ? { ...next, sort } : next;
};

/**
 * Sets or clears the tile's dimension or pivot ('' clears). A sort on a field
 * that is gone goes too, and so does autoGrain once the dimension is no longer
 * the Date (its checkbox only shows for date_date, so it could not be unticked).
 */
export const withField = (tile: SpecTile, key: 'dimension' | 'pivot', value: string): SpecTile => {
  const { [key]: _old, ...rest } = tile;
  const set: SpecTile = value ? { ...rest, [key]: value } : rest;
  if (key === 'dimension' && value !== 'date_date' && set.autoGrain !== undefined) {
    const { autoGrain: _g, ...noGrain } = set;
    return withSortKept(noGrain);
  }
  return withSortKept(set);
};

const withSortKept = (tile: SpecTile): SpecTile =>
  tile.sort && !tileFields(tile).includes(tile.sort.field) ? { ...tile, sort: undefined } : tile;

export interface DraftIssues {
  /** Template-level: title, description, tiles (none / too many). */
  template: Record<string, string>;
  /** By tile index, then by tile field (title, measures, dimension, pivot, sort, limit, key). */
  tiles: Record<number, Record<string, string>>;
  valid: boolean;
}

/** The shared zod's messages, first one per place. */
export const issuesOf = (spec: TemplateSpec): DraftIssues => {
  const parsed = TemplateSpecSchema.safeParse(spec);
  if (parsed.success) return { template: {}, tiles: {}, valid: true };
  return parsed.error.issues.reduce<DraftIssues>((acc, issue) => {
    const [head, index, field] = issue.path;
    if (head === 'tiles' && typeof index === 'number') {
      const where = typeof field === 'string' ? field : 'tile';
      const forTile = acc.tiles[index] ?? {};
      if (forTile[where]) return acc;
      return { ...acc, tiles: { ...acc.tiles, [index]: { ...forTile, [where]: issue.message } } };
    }
    const where = typeof head === 'string' ? head : 'template';
    return acc.template[where] ? acc : { ...acc, template: { ...acc.template, [where]: issue.message } };
  }, { template: {}, tiles: {}, valid: false });
};
